import { type DateTime } from 'luxon';
import { FullReportTable } from './FullReportTable';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans, useLingui } from '@lingui/react';
import { Suspense, useCallback, useState } from 'react';
import { DateInput } from '@startuptools/ui/DateInput';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useFullReportTableSuspenseQuery, useDocGenFullShareRegLazyQuery } from './FullReportTable.graphql';
import { apiClient } from '../../../../common/http';
import { PDFButton } from '@startuptools/business-ui/document/PDFPreviewButton';

export const FullReportInner = () => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const [date, setDate] = useState<DateTime | null>(null);
  const {
    data: { company, fullShareReg: rows },
  } = useFullReportTableSuspenseQuery({
    variables: { companyId, date: date ? date.toUTC(0, { keepLocalTime: true }).toString() : null },
    fetchPolicy: 'no-cache',
  });

  const [doGetFull] = useDocGenFullShareRegLazyQuery({ fetchPolicy: 'no-cache' });

  const handleExportPdf = useCallback(
    async (language: { primaryLang: boolean; secondaryLang: boolean }) => {
      const dateInput = date?.toUTC(0, { keepLocalTime: true }).toString() ?? null;
      const { data } = await doGetFull({
        variables: {
          companyId,
          date: dateInput,
          docLanguage: { primary_lang: language.primaryLang, secondary_lang: language.secondaryLang },
        },
      });
      const dg = data?.docGenFullShareReg.docGen;
      const { filename } = dg.docGenDetails;
      if (!filename) {
        throw new Error('no filename');
      }
      const blob = await apiClient.preview2({
        filename,
        docGens: [dg],
      });
      return { filename, blob };
    },
    [date, companyId, doGetFull],
  );

  return (
    <Stack gap={2}>
      <Typography level="h2">
        <Trans id="Share Register" />
      </Typography>
      <Stack direction="row" gap={1} alignSelf="end">
        <DateInput
          sx={{ width: '210px' }}
          label={<Trans id="Show until date" />}
          value={date}
          onChange={value => setDate(value)}
          slotProps={{ field: { clearable: true } }}
        />
        <PDFButton
          label={i18n.t('Export as PDF')}
          button={({ label, ...props }) => (
            <Button name={label} {...props}>
              <Trans id="Export as PDF" />
            </Button>
          )}
          generatePdf={handleExportPdf}
          allowBiLingual={false}
        />
      </Stack>
      <FullReportTable rows={rows} company={company} />
    </Stack>
  );
};

export const FullReport = () => {
  return (
    <Suspense>
      <FullReportInner />
    </Suspense>
  );
};
